/**
 * Сервіс повідомлень інтерфейсу (UX-13; components.md, розд. 10; states.md, розд. 5, 8, 9): тости,
 * черговість банерів і живий регіон — один стан на застосунок. Модулі не малюють своїх
 * «спливашок»: тост — `toast()`, банер — `<Banner>` або `showBanner()`, оголошення для програми
 * читання — `announce()`. Без React: викликати можна й з подій SSE чи обробника запиту.
 *
 * Правила:
 * - видно не більше двох тостів: помилку, що висить, і поточне підтвердження над нею; решта чекає
 *   в черзі. Однаковий тост (той самий `key`, а без нього — той самий текст і тон) не множиться,
 *   а оновлюється: лічильник +1, текст `text(count)`, таймер спочатку;
 * - помилка замінює лише підтвердження тієї самої дії (той самий `key`); чужі підтвердження зі
 *   «Скасувати» лишаються (П-08);
 * - банер: в області (`area`) видно лише один — найважливіший (менший `priority`), за рівності —
 *   новіший. Глобальні пріоритети — `GLOBAL_BANNER`;
 * - оголошення того самого тексту протягом `ANNOUNCE_DEDUP_MS` не повторюється: кілька областей,
 *   що водночас почали вантажитись, дають одне «Завантаження…» (UX-32).
 */

export type ToastTone = 'status' | 'alert';

export interface ToastAction {
  label: string;
  onAction: () => void;
}

export interface ToastInput {
  /** Текст або текст від кількості злитих тостів: `(n) => n > 1 ? \`Виконано ${n} задачі\` : …`. */
  text: string | ((count: number) => string);
  tone?: ToastTone;
  /** Не більше однієї дії (UX-13): «Скасувати», «Ще раз» або «Відкрити». */
  action?: ToastAction;
  /**
   * Ключ злиття: тости з тим самим ключем зливаються в один з лічильником. Дія злитого тосту —
   * дія останнього, тож вона має покривати всю пачку. Без ключа зливаються однакові тексти.
   */
  key?: string;
  /**
   * Таймер у тості (UX-19): `text(секунд лишилось)` оновлюється щосекунди лише на екрані, а
   * програма читання чує початок один раз. Такий тост не зникає сам — його прибирає той, хто
   * показав (`dismissToast`), коли очікування скінчилось.
   */
  countdown?: ToastCountdown;
}

export interface ToastCountdown {
  /** Кінець очікування, `Date.now()`-мс. */
  until: number;
  text: (seconds: number) => string;
}

export interface ToastItem {
  id: number;
  key: string;
  text: string;
  tone: ToastTone;
  action?: ToastAction | undefined;
  countdown?: ToastCountdown | undefined;
  count: number;
  /** Росте з кожним злиттям: показ тосту починається заново. */
  version: number;
  /** Коли тост востаннє з'явився чи оновився: `F8` веде до найновішого. */
  at: number;
}

export interface BannerAction {
  label: string;
  onAction: () => void;
}

/** Банер без React-вмісту — для модулів і подій поза деревом (`showBanner`). */
export interface BannerInput {
  area?: string;
  priority?: number;
  tone?: 'info' | 'warn' | 'danger';
  text: string;
  actions?: readonly BannerAction[];
}

interface BannerEntry {
  area: string;
  priority: number;
  seq: number;
  /** Є лише в банерів `showBanner`; `<Banner>` малює свій вміст сам. */
  data?: BannerInput;
}

export interface Announcement {
  text: string;
  assertive: boolean;
  /** Новий вузол на кожне оголошення: програма читання перечитує навіть той самий текст. */
  n: number;
}

/** Пріоритет глобальних банерів (UX-13): немає зв'язку → сесія → нова версія → оголошення. */
export const GLOBAL_BANNER = {
  offline: 0,
  session: 1,
  version: 2,
  announcement: 3,
  /** Пропозиція легкого режиму (UX-36) — найменш термінова: чекає, доки зникнуть інші. */
  lite: 4,
  /** Розбіжність поясу пристрою і профілю (PRF-04): підказка, не перешкода. */
  region: 5,
  /** «Оновіть браузер» (UX-52): раз на 7 днів, поступається всім. */
  browser: 6,
} as const;

export const TOAST_MS = 5_000;
export const ANNOUNCE_DEDUP_MS = 1_000;
/** Скільки оголошення лишається в регіоні: потім регіон чистий, у віртуальному буфері нема старого. */
export const ANNOUNCE_CLEAR_MS = 7_000;

let toasts: readonly ToastItem[] = [];
const banners = new Map<string, BannerEntry>();
let announcement: Announcement | null = null;
let lastAnnounced = { text: '', at: Number.NEGATIVE_INFINITY };
let clearTimer: ReturnType<typeof setTimeout> | undefined;
let nextId = 1;
let clock = 0;
let seq = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ---------- Тости ----------

const textOf = (input: ToastInput, count: number) =>
  typeof input.text === 'function' ? input.text(count) : input.text;

/**
 * Показати тост; повертає id. Той самий ключ і тон — злиття (лічильник +1). Помилка з ключем, що
 * вже є серед підтверджень, замінює те підтвердження на своєму місці: «Збережено» тієї самої дії
 * стає неправдою. Помилка без спільного ключа нічого не прибирає: чуже «Скасувати» лишається.
 */
export function toast(input: ToastInput): number {
  const tone = input.tone ?? 'status';
  const key = input.key ?? `${tone}:${textOf(input, 1)}`;
  const same = toasts.find((t) => t.key === key);
  if (same) {
    const merge = same.tone === tone;
    const count = merge ? same.count + 1 : 1;
    toasts = toasts.map((t) =>
      t === same
        ? {
            ...t,
            tone,
            count,
            text: textOf(input, count),
            action: input.action,
            countdown: input.countdown,
            version: t.version + 1,
            at: ++clock,
          }
        : t,
    );
    emit();
    return same.id;
  }
  const item: ToastItem = {
    id: nextId++,
    key,
    text: textOf(input, 1),
    tone,
    action: input.action,
    countdown: input.countdown,
    count: 1,
    version: 0,
    at: ++clock,
  };
  toasts = [...toasts, item];
  emit();
  return item.id;
}

/**
 * Видимі тости (components.md, розд. 10): не більше двох — перша помилка, що висить, і перше
 * підтвердження; решта чекає в черзі свого тону.
 */
export function visibleToasts(list: readonly ToastItem[]): {
  status?: ToastItem | undefined;
  alert?: ToastItem | undefined;
} {
  return {
    status: list.find((t) => t.tone === 'status'),
    alert: list.find((t) => t.tone === 'alert'),
  };
}

export function dismissToast(id: number) {
  if (!toasts.some((t) => t.id === id)) return;
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export const getToasts = () => toasts;

/**
 * Куди повернути фокус, коли тост, до якого перейшли клавішею `F8`, закрито: одне місце для тостів
 * дій і тостів-сповіщень (`features/notifications`), бо `F8` веде по колу через обидві зони.
 */
let focusReturn: HTMLElement | null = null;

export function rememberToastReturn(el: Element | null) {
  focusReturn = el instanceof HTMLElement ? el : null;
}

/** Елемент, з якого прийшли (якщо він ще в документі), інакше — вміст сторінки. */
export function takeToastReturn(): HTMLElement | null {
  const back = focusReturn?.isConnected ? focusReturn : document.getElementById('main');
  focusReturn = null;
  return back;
}

// ---------- Банери ----------

/** Зареєструвати банер (і `<Banner>`, і `showBanner`): той самий ключ оновлює, а не множить. */
export function registerBanner(
  key: string,
  entry: { area: string; priority: number; data?: BannerInput },
) {
  const prev = banners.get(key);
  banners.set(key, { ...entry, seq: prev?.seq ?? ++seq });
  emit();
}

export function hideBanner(key: string) {
  if (banners.delete(key)) emit();
}

/** Банер з текстом і діями для коду поза React: показ — до `hideBanner(key)`. */
export function showBanner(key: string, input: BannerInput) {
  registerBanner(key, {
    area: input.area ?? 'global',
    priority: input.priority ?? GLOBAL_BANNER.announcement,
    data: input,
  });
}

/** Ключ банера, якого зараз видно в області: найменший пріоритет, за рівності — новіший. */
export function topBanner(area: string): string | null {
  let best: [string, BannerEntry] | null = null;
  for (const entry of banners) {
    if (entry[1].area !== area) continue;
    if (
      !best ||
      entry[1].priority < best[1].priority ||
      (entry[1].priority === best[1].priority && entry[1].seq > best[1].seq)
    )
      best = entry;
  }
  return best ? best[0] : null;
}

export const bannerData = (key: string) => banners.get(key)?.data;

// ---------- Живий регіон ----------

/**
 * Оголосити текст програмі читання, не показуючи його (WCAG 4.1.3). `assertive` — лише для
 * помилок, що переривають роботу; решта чекає паузи в мовленні.
 */
export function announce(text: string, options: { assertive?: boolean } = {}) {
  const now = Date.now();
  if (text === lastAnnounced.text && now - lastAnnounced.at < ANNOUNCE_DEDUP_MS) return;
  lastAnnounced = { text, at: now };
  announcement = { text, assertive: Boolean(options.assertive), n: (announcement?.n ?? 0) + 1 };
  clearTimeout(clearTimer);
  clearTimer = setTimeout(() => {
    announcement = null;
    emit();
  }, ANNOUNCE_CLEAR_MS);
  emit();
}

export const getAnnouncement = () => announcement;

/** Прибрати оголошення, якщо воно ще те саме: банер зник — у буфері програми читання не лишаємо його текст. */
export function retractAnnouncement(text: string) {
  if (announcement?.text !== text) return;
  announcement = null;
  clearTimeout(clearTimer);
  emit();
}

/** Скинути стан між тестами. */
export function resetNotify() {
  toasts = [];
  focusReturn = null;
  banners.clear();
  announcement = null;
  lastAnnounced = { text: '', at: Number.NEGATIVE_INFINITY };
  clearTimeout(clearTimer);
  emit();
}
